/**
 * Studio Stephane Product Grid Scroll Handler
 * Captures scroll events to scroll through media grid before scrolling the page
 */

class StudioStephaneScrollHandler {
  constructor() {
    this.container = document.querySelector(
      ".studio-stephane-product-grid-container"
    );
    this.mediaGrid = document.querySelector(".studio-stephane-media-grid");

    if (!this.container || !this.mediaGrid) return;

    this.init();
  }

  init() {
    // Listen to wheel events on the container
    this.container.addEventListener("wheel", (e) => this.handleScroll(e), {
      passive: false,
    });
  }

  handleScroll(e) {
    const scrollingDown = e.deltaY > 0;
    const scrollingUp = e.deltaY < 0;

    const isAtTop = this.mediaGrid.scrollTop === 0;
    const isAtBottom =
      Math.abs(
        this.mediaGrid.scrollHeight -
          this.mediaGrid.scrollTop -
          this.mediaGrid.clientHeight
      ) < 1;

    // If scrolling down and media grid can still scroll, capture it
    if (scrollingDown && !isAtBottom) {
      e.preventDefault();
      this.mediaGrid.scrollTop += e.deltaY;
      return;
    }

    // If scrolling up and media grid is not at top, capture it
    if (scrollingUp && !isAtTop) {
      e.preventDefault();
      this.mediaGrid.scrollTop += e.deltaY;
      return;
    }

    // Otherwise, allow normal scroll behavior
  }
}

/**
 * Studio Stephane Product Form Observer
 * Observes when the product form enters/exits the viewport
 * Makes it fixed to bottom when fully visible and user scrolls past
 */
class StudioStephaneProductFormObserver {
  constructor() {
    this.productForm = document.querySelector(".studio-stephane-product-form");

    if (!this.productForm) return;

    this.isFixed = false;
    this.originalTop = null;
    this.lastScrollY = window.scrollY;
    this.isFullyVisible = false;

    this.init();
  }

  init() {
    // Store original position
    this.updateOriginalPosition();

    const options = {
      root: null, // viewport
      rootMargin: "0px",
      threshold: [0, 0.25, 0.5, 0.75, 1.0], // Trigger at multiple visibility points
    };

    this.observer = new IntersectionObserver(
      (entries) => this.handleIntersection(entries),
      options
    );

    this.observer.observe(this.productForm);

    // Listen to scroll events to handle fixing/unfixing
    window.addEventListener("scroll", () => this.handleScroll(), {
      passive: true,
    });

    // Update original position on resize
    window.addEventListener("resize", () => this.updateOriginalPosition(), {
      passive: true,
    });
  }

  updateOriginalPosition() {
    if (!this.isFixed) {
      const rect = this.productForm.getBoundingClientRect();
      this.originalTop = rect.top + window.scrollY;
    }
  }

  handleIntersection(entries) {
    entries.forEach((entry) => {
      const visibilityPercent = Math.round(entry.intersectionRatio * 100);

      this.productForm.setAttribute(
        "data-visible",
        visibilityPercent.toString()
      );

      if (entry.isIntersecting) {
        this.productForm.classList.add("is-visible");

        // Track if element is fully visible
        this.isFullyVisible = visibilityPercent === 100;

        console.log(
          `Product form is ${visibilityPercent}% visible in viewport`
        );
      } else {
        this.productForm.classList.remove("is-visible");
        this.isFullyVisible = false;

        console.log("Product form is not visible in viewport");
      }
    });
  }

  handleScroll() {
    const currentScrollY = window.scrollY;
    const scrollingDown = currentScrollY > this.lastScrollY;
    const scrollingUp = currentScrollY < this.lastScrollY;

    // Check if we're at or above the original position
    const isAtOriginalPosition =
      currentScrollY <= this.originalTop - window.innerHeight;

    if (this.isFullyVisible && scrollingDown && !this.isFixed) {
      // Element is fully visible and user is scrolling down -> make it fixed
      this.makeFixed();
    } else if (this.isFixed && scrollingUp && isAtOriginalPosition) {
      // User scrolled back to original position -> unfix it
      this.makeUnfixed();
    }

    this.lastScrollY = currentScrollY;
  }

  makeFixed() {
    if (this.isFixed) return;

    this.isFixed = true;
    this.productForm.classList.add("is-fixed-bottom");
    console.log("Product form fixed to bottom");
  }

  makeUnfixed() {
    if (!this.isFixed) return;

    this.isFixed = false;
    this.productForm.classList.remove("is-fixed-bottom");
    console.log("Product form unfixed");
  }

  disconnect() {
    if (this.observer) {
      this.observer.disconnect();
    }
  }
}

// Initialize when DOM is ready
if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", () => {
    new StudioStephaneScrollHandler();
    new StudioStephaneProductFormObserver();
  });
} else {
  new StudioStephaneScrollHandler();
  new StudioStephaneProductFormObserver();
}
